import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAuth } from './AuthContext';
import { apiJson, apiUpload } from './api';
import { useSEO } from './useSEO';
import DebugPapersView from './DebugPapersView';
import HomeCalendarsStrip from './calendars/HomeCalendarsStrip';


function Home() {
  const { user, token } = useAuth();
  const [level, setLevel] = useState(null);
  const [subject, setSubject] = useState(null);
  const [subjectsList, setSubjectsList] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsError, setSubjectsError] = useState(null);

  const [files, setFiles] = useState({});
  const [board, setBoard] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  // Map of file path (e.g. "a-level/Chemistry/AQA/Past Papers/2023/AQA ... QP.pdf") -> paperId
  const [paperByPath, setPaperByPath] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [adminSelectedFiles, setAdminSelectedFiles] = useState([]);
  const [adminTargetCategory, setAdminTargetCategory] = useState('');
  const [adminSubfolder, setAdminSubfolder] = useState('');
  const [adminStatus, setAdminStatus] = useState('');
  const [adminError, setAdminError] = useState('');
  const [adminUploading, setAdminUploading] = useState(false);
  const [busyPath, setBusyPath] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [categoriesList, setCategoriesList] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);

  const API_BASE = useMemo(() => (process.env.REACT_APP_API_URL || '').replace(/\/$/, ''), []);
  const buildUrl = useCallback((path) => `${API_BASE}${path}`, [API_BASE]);
  const isAdmin = !!user?.isAdmin;

  // Admin-only "Debug Mode" — when on, replaces the regular file list with a
  // drag-and-drop board for managing papers (linking files to QP/MS/Model
  // slots, renaming, deleting). Persisted per-browser.
  const [debugMode, setDebugMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('srh_debug_mode') === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (debugMode) localStorage.setItem('srh_debug_mode', '1');
    else localStorage.removeItem('srh_debug_mode');
  }, [debugMode]);
  // If the user loses admin (or never had it), force debug off.
  useEffect(() => { if (!isAdmin && debugMode) setDebugMode(false); }, [isAdmin, debugMode]);

  const fetchSubjects = useCallback((lvl) => {
    if (!lvl) return;
    setSubjectsLoading(true);
    setSubjectsError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 7000);
    const safeLevel = encodeURIComponent(lvl);

    fetch(buildUrl(`/api/subjects/${safeLevel}`), { signal: controller.signal })
      .then(res => {
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error(`Server error ${res.status}`);
        return res.json();
      })
      .then(data => {
        setSubjectsList(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Failed to fetch subjects:', err);
        if (err.name === 'AbortError') {
          setSubjectsError('Request timed out. Please try again.');
        } else {
          setSubjectsError(err.message || 'Failed to fetch subjects');
        }
        setSubjectsList([]);
      })
      .finally(() => setSubjectsLoading(false));
  }, [buildUrl]);

  useEffect(() => {
    if (selectedCategory && selectedCategory !== 'All') {
      setAdminTargetCategory(selectedCategory);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (!board) {
      setAdminSelectedFiles([]);
      setAdminStatus('');
      setAdminError('');
      setAdminTargetCategory('');
      setAdminSubfolder('');
    }
  }, [board]);

  useEffect(() => {
    if (!adminTargetCategory && categoriesList.length) {
      setAdminTargetCategory(categoriesList[0]);
    }
  }, [categoriesList, adminTargetCategory]);

  const handleAdminFileInput = (event) => {
    const incoming = Array.from(event.target.files || []);
    if (!incoming.length) return;
    setAdminSelectedFiles((prev) => [...prev, ...incoming]);
    setAdminStatus('');
    setAdminError('');
  };

  const handleAdminUpload = async (event) => {
    event?.preventDefault?.();
    if (!isAdmin) {
      setAdminError('Admin access required');
      return;
    }
    if (!level || !subject || !board) {
      setAdminError('Choose level, subject, and board first');
      return;
    }

    const targetCategory = adminTargetCategory || (selectedCategory !== 'All' ? selectedCategory : '');
    if (!targetCategory) {
      setAdminError('Pick a category to upload into');
      return;
    }
    if (!adminSelectedFiles.length) {
      setAdminError('Choose at least one file');
      return;
    }

    setAdminUploading(true);
    setAdminError('');
    setAdminStatus('Uploading…');

    try {
      for (const file of adminSelectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('level', level);
        formData.append('subject', subject);
        formData.append('board', board);
        formData.append('category', targetCategory);
        if (adminSubfolder.trim()) formData.append('subfolder', adminSubfolder.trim());
        await apiUpload('/api/admin/upload', formData, { token });
      }
      setAdminStatus(`Uploaded ${adminSelectedFiles.length} file${adminSelectedFiles.length === 1 ? '' : 's'}.`);
      setAdminSelectedFiles([]);
      setRefreshTick((x) => x + 1);
    } catch (err) {
      setAdminError(err.message || 'Upload failed');
    } finally {
      setAdminUploading(false);
    }
  };

  const deleteFile = async (category, filename, subfolder = '') => {
    if (!isAdmin || !token) return;
    const label = [category, subfolder, filename].filter(Boolean).join(' / ');
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;

    setBusyPath(`${category}|${subfolder}|${filename}`);
    setAdminError('');

    try {
      const relPath = [level, subject, board, category, subfolder, filename]
        .filter(Boolean)
        .join('/');
      await apiJson('/api/admin/files/delete-by-path', {
        method: 'POST',
        body: { relPath },
        token,
      });
      setRefreshTick((x) => x + 1);
    } catch (err) {
      setAdminError(err.message || 'Delete failed');
    } finally {
      setBusyPath(null);
    }
  };

  const renameFile = async (category, filename, subfolder = '') => {
    if (!isAdmin || !token) return;
    const proposed = window.prompt('New filename (include extension)', filename);
    if (!proposed || proposed === filename) return;

    setBusyPath(`${category}|${subfolder}|${filename}`);
    setAdminError('');

    try {
      const relPath = [level, subject, board, category, subfolder, filename]
        .filter(Boolean)
        .join('/');
      await apiJson('/api/admin/files/rename-by-path', {
        method: 'POST',
        body: { relPath, newName: proposed },
        token,
      });
      setRefreshTick((x) => x + 1);
    } catch (err) {
      setAdminError(err.message || 'Rename failed');
    } finally {
      setBusyPath(null);
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I find GCSE or A-Level past papers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Choose a level, pick your subject and exam board, then open the Past Papers section to view and download papers and mark schemes."
        }
      },
      {
        "@type": "Question",
        "name": "Do you have topic questions and mark schemes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Each subject has Questions by Topic folders with matching mark schemes so you can practice and self-mark."
        }
      },
      {
        "@type": "Question",
        "name": "Are resources available for AQA, Edexcel, and OCR?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We list resources by exam board; pick your level and subject to see which boards have available papers and topic packs."
        }
      }
    ]
  };

  useSEO({
    title: subject && level
      ? `${subject} (${level === 'a-level' ? 'A-Level' : level === 'as-level' ? 'AS-Level' : level.toUpperCase()}) resources`
      : level
        ? `${level === 'a-level' ? 'A-Level' : level === 'as-level' ? 'AS-Level' : level.toUpperCase()} resources`
        : 'Resource library',
    description: 'GCSE and A-Level revision resources: past papers, mark schemes, topic questions, and printable practice for AQA, Edexcel, and OCR.',
    canonicalPath: '/',
    schema: [faqSchema]
  });

  // Subject scroller ref and chevron visibility
  const subjectsRef = useRef(null);
  const [showLeftChev, setShowLeftChev] = useState(false);
  const [showRightChev, setShowRightChev] = useState(false);

  const scrollSubjects = (dir) => {
    const el = subjectsRef.current;
    if (!el) return;
    const delta = Math.max(160, Math.round(el.clientWidth * 0.6));
    el.scrollBy({ left: dir === 'left' ? -delta : delta, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = subjectsRef.current;
    if (!el) return;
    const update = () => {
      setShowLeftChev(el.scrollLeft > 8);
      setShowRightChev(el.scrollWidth > el.clientWidth + el.scrollLeft + 8);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    // re-run when level/subject/subjectsList changes so visibility recalculates
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [level, subject, subjectsList]);

  useEffect(() => {
    if (!level) {
      setSubjectsList([]);
      setSubjectsError(null);
      return;
    }

    fetchSubjects(level);
  }, [level, fetchSubjects]);


  

  // subjects list is fetched dynamically from the backend (based on /resources/<level>/ folders)

  const boards = useMemo(() => ["AQA", "Edexcel", "OCR"], []);

  // Only show boards that actually have folders for the selected level/subject
  const [availableBoards, setAvailableBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [boardsError, setBoardsError] = useState(null);

  useEffect(() => {
    // Reset when there's no level/subject
    if (!level || !subject) {
      setAvailableBoards([]);
      setBoardsError(null);
      setBoardsLoading(false);
      return;
    }

    setBoardsLoading(true);
    setBoardsError(null);

    const safeLevel = encodeURIComponent(level);
    const safeSubject = encodeURIComponent(subject);
    let cancelled = false;

    // Helper to fetch with timeout
    const fetchWithTimeout = (url, timeout = 5000) => {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      return fetch(url, { signal: controller.signal })
        .finally(() => clearTimeout(id));
    };

    const checks = boards.map(b => {
      const safeBoard = encodeURIComponent(b);
      return fetchWithTimeout(buildUrl(`/api/categories/${safeLevel}/${safeSubject}/${safeBoard}`), 5000)
        .then(res => res.ok ? res.json() : [])
        .then(data => ({ board: b, has: Array.isArray(data) && data.length > 0 }))
        .catch(err => {
          // Treat aborted or failed fetch as no data for that board
          console.error('Failed to check board availability for', b, err?.name || err);
          return { board: b, has: false };
        });
    });

    // Safety timer to avoid stuck loading state
    const safetyTimer = setTimeout(() => {
      if (!cancelled) {
        console.warn('Board availability check timed out - clearing loading state');
        setBoardsLoading(false);
        setBoardsError(prev => prev || 'Board check timed out');
      }
    }, 6000);

    // Use allSettled so one failing request doesn't block others
    Promise.allSettled(checks)
      .then(results => {
        if (cancelled) return;
        const boardsAvailable = results
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value)
          .filter(Boolean)
          .filter(r => r.has)
          .map(r => r.board);
        console.log('Available boards:', boardsAvailable);
        setAvailableBoards(boardsAvailable);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Error checking boards:', err);
        setBoardsError('Failed to check available boards');
        setAvailableBoards([]);
      })
      .finally(() => {
        if (!cancelled) setBoardsLoading(false);
        clearTimeout(safetyTimer);
      });

    return () => { cancelled = true; clearTimeout(safetyTimer); };
  }, [level, subject, buildUrl, boards]);

  // Page background is controlled by CSS variables (see :root and [data-theme] rules in App.css).
  // The body background will switch between light and dark modes; individual subject backgrounds are no longer used for the page.
  // When a board is chosen, fetch available categories and their files
  useEffect(() => {
    if (level && subject && board) {
      setCategoriesError(null);
      setCategoriesLoading(true);
      setFetchError(null);
      setLoadingFiles(true);

      const safeLevel = encodeURIComponent(level);
      const safeSubject = encodeURIComponent(subject);
      const safeBoard = encodeURIComponent(board);

      // Fetch dynamic categories (folder names)
      fetch(buildUrl(`/api/categories/${safeLevel}/${safeSubject}/${safeBoard}`))
        .then(res => {
          if (!res.ok) throw new Error(`Server error ${res.status}`);
          return res.json();
        })
        .then(catData => {
          setCategoriesList(Array.isArray(catData) ? catData : []);
          // Reset selected category if it's not present
          setSelectedCategory(prev => (prev === 'All' || (Array.isArray(catData) && catData.includes(prev)) ? prev : 'All'));
        })
        .catch(err => {
          console.error('Failed to fetch categories:', err);
          setCategoriesError(err.message || 'Failed to fetch categories');
          setCategoriesList([]);
        })
        .finally(() => setCategoriesLoading(false));

      // Fetch files for all categories (dynamic)
      fetch(buildUrl(`/api/resources/${safeLevel}/${safeSubject}/${safeBoard}`))
        .then(res => {
          if (!res.ok) throw new Error(`Server error ${res.status}`);
          return res.json();
        })
        .then(data => {
          // data is a mapping { categoryName: { files: [...], subfolders: {...} } }
          setFiles(data || {});
        })
        .catch(err => {
          console.error(`Failed to fetch resources:`, err);
          setFetchError(err.message || 'Failed to fetch resources');
          setFiles({});
        })
        .finally(() => setLoadingFiles(false));

      // Fetch papers for this level/subject/board so we can show a "Practice"
      // button next to any file that's been linked into a paper grouping.
      const paperParams = new URLSearchParams({ level, subject, board });
      fetch(buildUrl(`/api/papers?${paperParams.toString()}`))
        .then(res => res.ok ? res.json() : { papers: [] })
        .then(data => {
          const map = {};
          for (const p of data.papers || []) {
            for (const role of Object.keys(p.files || {})) {
              const f = p.files[role];
              if (f?.filePath) map[f.filePath] = p.id;
            }
          }
          setPaperByPath(map);
        })
        .catch(() => setPaperByPath({}));
    }
  }, [level, subject, board, refreshTick, buildUrl]);



    
  

  const renderSection = (title) => {
    const catData = files[title];
    // backward compatibility: if catData is array (old format), treat as files
    const directFiles = Array.isArray(catData) ? catData : (catData && Array.isArray(catData.files) ? catData.files : []);
    const subfolders = catData && !Array.isArray(catData) && catData.subfolders ? catData.subfolders : {};

    if (title === 'Notes') {
      return (
        <details className="resource-section" open>
          <summary>
            <div className="resource-title">Notes</div>
            <div className="resource-count"></div>
            <div style={{ marginLeft: 'auto' }}>
              <a
                className="section-buy"
                href={`/notes?level=${encodeURIComponent(level)}&subject=${encodeURIComponent(subject)}&board=${encodeURIComponent(board || '')}`}
                aria-label={`Open notes for ${subject} ${level} ${board || ''}`}
              >Open</a>
            </div>
          </summary>
          <div style={{ padding: '8px 0 4px' }}>
            <p style={{ margin: 0, color: 'var(--muted)' }}>
              Browse generated topic-by-topic notes for this subject and board.
            </p>
          </div>
        </details>
      );
    }

    return (
      <details className="resource-section" open>
        <summary>
          <div className="resource-title">{title}</div>
          <div className="resource-count">{(directFiles.length + Object.values(subfolders).reduce((s, a) => s + (Array.isArray(a) ? a.length : 0), 0)) ? `(${directFiles.length + Object.values(subfolders).reduce((s, a) => s + (Array.isArray(a) ? a.length : 0), 0)})` : ''}</div>
          <div style={{ marginLeft: 'auto' }}>
            <a
              className="section-buy"
              href={`/payment?level=${encodeURIComponent(level)}&subject=${encodeURIComponent(subject)}&board=${encodeURIComponent(board || '')}&category=${encodeURIComponent(title)}`}
              aria-label={`Buy ${title} for ${subject} ${level} ${board || ''}`}
            >Buy</a>
          </div>
        </summary>

        {loadingFiles ? (
          <div aria-busy="true" aria-live="polite" className="skeleton-list">
            <div className="skeleton-line" style={{ width: '80%' }} />
            <div className="skeleton-line" style={{ width: '60%' }} />
            <div className="skeleton-line" style={{ width: '70%' }} />
          </div>
        ) : fetchError ? (
          <p style={{ color: 'red' }}>Could not load resources: {fetchError}</p>
        ) : (
          <div>
            <ul>
              {directFiles.length > 0 && directFiles.map((file, index) => {
                const ext = file.includes('.') ? file.split('.').pop().toUpperCase() : '';
                const fileUrl = buildUrl(`/files/${encodeURIComponent(level)}/${encodeURIComponent(subject)}/${encodeURIComponent(board)}/${encodeURIComponent(title)}/${encodeURIComponent(file)}`);
                const filePathKey = `${level}/${subject}/${board}/${title}/${file}`;
                const paperId = paperByPath[filePathKey];
                const pathKey = `${title}||${file}`;
                return (
                  <li key={"f-"+index} className="file-row">
                    <a className="file-link" href={fileUrl} target="_blank" rel="noopener noreferrer">
                      {file}
                    </a>
                    <span className="file-type">{ext}</span>
                    {paperId && (
                      <a className="practice-btn" href={`/viewer/${paperId}`} target="_blank" rel="noopener noreferrer" aria-label={`Practice ${file}`}>
                        Practice
                      </a>
                    )}
                    <a className="download-btn" href={fileUrl} download aria-label={`Download ${file}`}>
                      <svg className="download-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                        <path d="M12 3v13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </a>
                    {isAdmin && (
                      <div className="admin-file-actions">
                        <button
                          className="admin-link"
                          onClick={() => renameFile(title, file)}
                          disabled={busyPath === pathKey}
                        >
                          Rename
                        </button>
                        <button
                          className="admin-link danger"
                          onClick={() => deleteFile(title, file)}
                          disabled={busyPath === pathKey}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}

              {Object.keys(subfolders).length > 0 && Object.keys(subfolders).map(sub => {
                const subFiles = Array.isArray(subfolders[sub]) ? subfolders[sub] : [];
                return (
                  <React.Fragment key={"frag-"+sub}>
                    <li key={"s-"+sub} className="subheading-row">
                      <div className="subheading-title"><span className="sub-icon" aria-hidden>📁</span><span className="sub-title-text">{sub}</span><span className="sub-count-pill">{subFiles.length}</span></div>
                    </li>

                    <li key={"sf-wrap-"+sub} className="subfiles-wrapper">
                      <ul className={`subfiles open`}>
                        {subFiles.length > 0 ? subFiles.map((file, idx) => {
                          const ext = file.includes('.') ? file.split('.').pop().toUpperCase() : '';
                          const fileUrl = buildUrl(`/files/${encodeURIComponent(level)}/${encodeURIComponent(subject)}/${encodeURIComponent(board)}/${encodeURIComponent(title)}/${encodeURIComponent(sub)}/${encodeURIComponent(file)}`);
                          const filePathKey = `${level}/${subject}/${board}/${title}/${sub}/${file}`;
                          const paperId = paperByPath[filePathKey];
                          const pathKey = `${title}|${sub}|${file}`;
                          return (
                            <li key={"sf-"+idx} className="file-row subfile-row">
                              <a className="file-link" href={fileUrl} target="_blank" rel="noopener noreferrer">
                                {file}
                              </a>
                              <span className="file-type">{ext}</span>
                              {paperId && (
                                <a className="practice-btn" href={`/viewer/${paperId}`} target="_blank" rel="noopener noreferrer" aria-label={`Practice ${file}`}>
                                  Practice
                                </a>
                              )}
                              <a className="download-btn" href={fileUrl} download aria-label={`Download ${file}`}>
                                <svg className="download-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                  <path d="M12 3v13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </a>
                              {isAdmin && (
                                <div className="admin-file-actions">
                                  <button
                                    className="admin-link"
                                    onClick={() => renameFile(title, file, sub)}
                                    disabled={busyPath === pathKey}
                                  >
                                    Rename
                                  </button>
                                  <button
                                    className="admin-link danger"
                                    onClick={() => deleteFile(title, file, sub)}
                                    disabled={busyPath === pathKey}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </li>
                          );
                        }) : <li className="subfile-empty">No resources in this section.</li>}
                      </ul>
                    </li>
                  </React.Fragment>
                );
              })}

              {directFiles.length === 0 && Object.keys(subfolders).length === 0 && <li>No resources available.</li>}
            </ul>
          </div>
        )}

      </details>
    );
  };

  

  return (
    <div
      className="app"
      style={{
        transition: "background-color 0.8s ease-in-out",
        minHeight: "100vh"
      }}>
      <header className="hero">
        <h1>Welcome to the Resource Hub 📚</h1>
        <p>Find past papers, assessments, and more.</p>
      </header>

      {!subject && <HomeCalendarsStrip />}

      <div className="levels" role="tablist" aria-label="Education level">
        <button className={`level ${level === 'gcse' ? 'active' : ''}`} onClick={() => { setLevel('gcse'); setSubject(null); setFiles({}); setBoard(null); setSelectedCategory('All'); }} aria-pressed={level === 'gcse'}>GCSE</button>
        <button className={`level ${level === 'igcse' ? 'active' : ''}`} onClick={() => { setLevel('igcse'); setSubject(null); setFiles({}); setBoard(null); setSelectedCategory('All'); }} aria-pressed={level === 'igcse'}>IGCSE</button>
        <button className={`level ${level === 'a-level' ? 'active' : ''}`} onClick={() => { setLevel('a-level'); setSubject(null); setFiles({}); setBoard(null); setSelectedCategory('All'); }} aria-pressed={level === 'a-level'}>A-Level</button>
        <button className={`level ${level === 'as-level' ? 'active' : ''}`} onClick={() => { setLevel('as-level'); setSubject(null); setFiles({}); setBoard(null); setSelectedCategory('All'); }} aria-pressed={level === 'as-level'}>AS-Level</button>
      </div>

      {level && (
        <div className="subjects-wrap" style={{ position: 'relative' }}>
        <button
          className={`chev left ${showLeftChev ? 'visible' : ''}`}
          onClick={() => scrollSubjects('left')}
          aria-hidden={!showLeftChev}
          aria-label="Scroll subjects left"
        >‹</button>

          <div className="subjects" ref={subjectsRef}>
          {subjectsLoading ? (
            <div style={{ display: 'flex', gap: 12, padding: '6px 8px' }}>
              <div className="skeleton-line" style={{ width: 120, height: 36 }} />
              <div className="skeleton-line" style={{ width: 100, height: 36 }} />
              <div className="skeleton-line" style={{ width: 140, height: 36 }} />
            </div>
          ) : subjectsError ? (
            <div style={{ color: 'red', display: 'flex', gap: 12, alignItems: 'center' }}>
              <div>Failed to load subjects: {subjectsError}</div>
              <button onClick={() => fetchSubjects(level)} style={{ background: '#1d3557', color: '#fff', padding: '6px 10px', borderRadius: 8 }}>Retry</button>
            </div>
          ) : (
            subjectsList.map((subj) => (
              <button
                key={subj}
                className={`level subject-button ${level} ${subj} ${subject === subj ? 'active' : ''}`}
                onClick={() => { setSubject(subj); setBoard(null); setSelectedCategory('All'); }}
                aria-pressed={subject === subj}

              >
                {subj}
              </button>
            ))
          )}
        </div>

        <button
          className={`chev right ${showRightChev ? 'visible' : ''}`}
          onClick={() => scrollSubjects('right')}
          aria-hidden={!showRightChev}
          aria-label="Scroll subjects right"
        >›</button>
      </div> 
      )}

      {subject && (
        <div className="exam-boards" style={{ marginTop: "1rem", display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap" }} aria-live="polite">
          {boardsLoading ? (
            <div style={{ color: 'var(--muted)' }}>Checking available boards...</div>
          ) : boardsError ? (
            <div style={{ color: '#dc2626' }}>Failed to check boards</div>
          ) : availableBoards.length === 0 ? (
            <div style={{ color: 'var(--muted)' }}>No exam boards available for this subject yet.</div>
          ) : (
            availableBoards.map((b) => (
              <button
                key={b}
                className={`board-chip ${board === b ? 'active' : ''}`}
                onClick={() => setBoard(b)}
                aria-pressed={board === b}
              >
                {b}
              </button>
            ))
          )}
        </div>
      )}

      {subject && (
        <div style={{ marginTop: "3rem" }}>
          <h2 style={{ textAlign: "center" }}>{level.toUpperCase()} - {subject}</h2>

          {!board ? (
            <p style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--muted)' }}>Please select an exam board above to view resources.</p>
          ) : (
            <>
              {/* <BuyButton level={level} subject={subject} board={board} /> */}
              <hr></hr>

              {isAdmin && (
                <div className="admin-debug-toggle-row">
                  <label className="admin-debug-toggle">
                    <input
                      type="checkbox"
                      checked={debugMode}
                      onChange={(e) => setDebugMode(e.target.checked)}
                    />
                    <span>Debug mode</span>
                    <span className="admin-debug-hint">
                      {debugMode ? 'Drag-and-drop board for linking files to papers.' : 'Admin-only · turn on to manage papers & files.'}
                    </span>
                  </label>
                </div>
              )}

              {isAdmin && !debugMode && (
                <div className="admin-inline-card">
                  <div className="admin-inline-row">
                    <label className="admin-inline-field">
                      <span>Target category</span>
                      <select
                        value={adminTargetCategory || ''}
                        onChange={(e) => setAdminTargetCategory(e.target.value)}
                      >
                        <option value="">Select a category</option>
                        {categoriesList.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </label>

                    <label className="admin-inline-field">
                      <span>Subfolder (optional)</span>
                      <input
                        type="text"
                        value={adminSubfolder}
                        onChange={(e) => setAdminSubfolder(e.target.value)}
                        placeholder="e.g. 2023 Papers"
                      />
                    </label>
                  </div>

                  <div className="admin-inline-row">
                    <label className="admin-inline-field">
                      <span>Files to upload</span>
                      <input type="file" multiple onChange={handleAdminFileInput} />
                    </label>

                    <div className="admin-inline-actions">
                      <button
                        className="admin-btn primary"
                        onClick={handleAdminUpload}
                        disabled={adminUploading || !adminSelectedFiles.length || !adminTargetCategory}
                      >
                        {adminUploading ? 'Uploading…' : 'Upload files'}
                      </button>
                      <button
                        className="admin-btn ghost"
                        onClick={() => setAdminSelectedFiles([])}
                        disabled={!adminSelectedFiles.length || adminUploading}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {adminSelectedFiles.length > 0 && (
                    <div className="admin-selected-list" aria-live="polite">
                      {adminSelectedFiles.map((f, idx) => (
                        <span key={`${f.name}-${idx}`} className="admin-selected-chip">{f.name}</span>
                      ))}
                    </div>
                  )}

                  {adminStatus && <div className="admin-status success">{adminStatus}</div>}
                  {adminError && <div className="admin-status error">{adminError}</div>}
                </div>
              )}

              <div className="category-chips" role="tablist" aria-label="Resource categories">
                <button className={`category-chip ${selectedCategory === 'All' ? 'active' : ''}`} onClick={() => setSelectedCategory('All')} aria-pressed={selectedCategory === 'All'}>All</button>
                {categoriesLoading ? (
                  <div style={{ display: 'inline-flex', gap: 8 }}>
                    <div className="skeleton-line" style={{ width: 80, height: 28 }} />
                    <div className="skeleton-line" style={{ width: 70, height: 28 }} />
                  </div>
                ) : categoriesError ? (
                  <div style={{ color: 'red' }}>Failed to load categories</div>
                ) : (
                  categoriesList.map(cat => (
                    <button key={cat} className={`category-chip ${selectedCategory === cat ? 'active' : ''}`} onClick={() => setSelectedCategory(cat)} aria-pressed={selectedCategory === cat}>{cat}</button>
                  ))
                )}
              </div>

              {debugMode && isAdmin ? (
                <DebugPapersView
                  level={level}
                  subject={subject}
                  board={board}
                  category={selectedCategory !== 'All' ? selectedCategory : ''}
                  token={token}
                  onChanged={() => setRefreshTick((x) => x + 1)}
                />
              ) : (
                <div className="resource-grid" aria-hidden={!board}>
                  {(selectedCategory === 'All' ? (categoriesList.length ? categoriesList : Object.keys(files)) : [selectedCategory]).map(cat => renderSection(cat))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Home;
